import React, { useEffect, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import "../styles/reporting.css";
import {
  getTotalTransactions,
  getSettledTransactions,
  getUnsettledTransactions,
  getDisputedTransactions,
  totalTransactions,
  settledTransactions,
  unsettledTransactions,
  disputedTransactions,
  todayTransactions,
  getTodayTransactions,
  getVoidedTransactions,
  voidedTransactions,
  getAllMerchants,
  getTransactionMessages,
  getDeclinedTransactions,
  declinedTransactions,
  getDailyVolumeReportApi,
  getMerchantBatchReport,
} from "../api/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaChartBar, FaCreditCard, FaStore } from "react-icons/fa";

const validationSchema = Yup.object().shape({
  mid: Yup.string()
    .nullable()
    .matches(/^\d{15}$/, "MID must be exactly 15 digits")
    .notRequired(),
  tid: Yup.string()
    .nullable()
    .matches(/^\d{8}$/, "TID must be exactly 8 digits")
    .notRequired(),
  transactionType: Yup.string().required("Transaction type is required"),
});

const Reporting = ({ isSidebarOpen }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [modeType, setModeType] = useState("DVR");
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [tids, setTids] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [dvtLoading, setDvtLoading] = useState(false);
  const [merchantReport, setMerchantReport] = useState(null);
  const [merchantReportLoading, setMerchantReportLoading] = useState(false);

  const transactionOptions = [
    { value: "Total", label: "Total" },
    { value: "Settled", label: "Settled" },
    { value: "Un-Settled", label: "Un-Settled" },
    { value: "Disputed", label: "Disputed" },
    { value: "Declined", label: "Declined" },
    { value: "Voided", label: "Voided" },
    { value: "Today's", label: "Today's" },
  ];
  // Formik setup
  const formik = useFormik({
    initialValues: {
      mid: "",
      tid: "",
      transactionType: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await fetchTransactions(values);
    },
  });
  // Fetch total transactions on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await getAllMerchants();
        if (resp?.data) {
          const filteredMerchants = resp.data.filter(
            (m) => m.terminals && m.terminals.length > 0,
          );
          setMerchants(filteredMerchants);
        }
        const res = await getTotalTransactions();
        if (res?.result?.data) {
          const formattedData = res.result.data.map((item) => {
            const newItem = {};
            for (const key in item) newItem[key] = item[key] ?? "--";
            return newItem;
          });
          setTransactions(formattedData);
          setFilteredData(formattedData);
        }
      } catch (err) {
        console.error("Error fetching reporting data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update TIDs when MID changes
  useEffect(() => {
    if (formik.values.mid) {
      const merchant = merchants.find((m) => m.MID === formik.values.mid);
      if (merchant && merchant.terminals) {
        setTids(
          merchant.terminals.map((t) => ({
            label: t.TID,
            value: t.TID,
          })),
        );
      } else {
        setTids([]);
      }
    } else {
      setTids([]);
    }
  }, [formik.values.mid, merchants]);

  // Utility to decide which API to call
  const fetchTransactions = async (values) => {
    const { transactionType, mid, tid } = values;
    let response;
    try {
      if (!mid && !tid) {
        switch (transactionType) {
          case "Total":
            response = await getTotalTransactions();
            break;
          case "Today's":
            response = await getTodayTransactions();
            break;
          case "Settled":
            response = await getSettledTransactions();
            break;
          case "Un-Settled":
            response = await getUnsettledTransactions();
            break;
          case "Declined":
            response = await getDeclinedTransactions();
            break;

          case "Voided":
            response = await getVoidedTransactions();
            break;
          case "Disputed":
            response = await getDisputedTransactions();
            break;
          default:
            response = await getTotalTransactions();
        }
      } else {
        switch (transactionType) {
          case "Total":
            response = await totalTransactions(tid, mid);
            break;
          case "Today's":
            response = await todayTransactions(tid, mid);
            break;
          case "Settled":
            response = await settledTransactions(tid, mid);
            break;
          case "Un-Settled":
            response = await unsettledTransactions(tid, mid);
            break;
          case "Declined":
            response = await declinedTransactions(tid, mid);
            break;
          case "Voided":
            response = await voidedTransactions(tid, mid);
            break;
          case "Disputed":
            response = await disputedTransactions(tid, mid);
            break;
          default:
            response = await totalTransactions(tid, mid);
        }
      }

      if (response?.result?.data) {
        const formattedData = response.result.data.map((item) => {
          const newItem = {};
          for (const key in item) newItem[key] = item[key] ?? "--";
          return newItem;
        });
        setFilteredData(formattedData);
      } else {
        setFilteredData([]);
      }
    } catch (err) {
      console.error("Error filtering transactions:", err);
      setFilteredData([]);
    }
  };
  const handleFetchMerchantReport = async () => {
    const { mid, tid } = formik.values;
    if (!mid || !tid) return;
    setMerchantReportLoading(true);
    try {
      const res = await getMerchantBatchReport(mid, tid); // pass both
      if (res.isSuccess) setMerchantReport(res);
    } catch (err) {
      console.error(err);
    } finally {
      setMerchantReportLoading(false);
    }
  };
  const handleExportCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      alert("No data available to export");
      return;
    }

    let dataToExport = [...filteredData];

    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = new Date(dateRange[0]);
      // ✅ Fix: set end date to end of day (23:59:59)
      const end = new Date(dateRange[1]);
      end.setHours(23, 59, 59, 999);

      dataToExport = dataToExport.filter((item) => {
        if (!item.date || item.date === "--") return false;
        const itemDate = new Date(item.date);
        return itemDate >= start && itemDate <= end;
      });

      if (dataToExport.length === 0) {
        alert("No transactions found for the selected date range");
        return;
      }
    }

    const headers = Object.keys(dataToExport[0]).filter(
      (key) => key !== "ReportingFlag",
    );
    const csvRows = [headers.join(",")];

    dataToExport.forEach((row) => {
      const values = headers.map((field) => {
        let val = row[field];
        if (val === null || val === undefined) val = "";

        if (
          ["SettlementStatus", "ForcedSettledStatus", "VoidStatus"].includes(
            field,
          )
        ) {
          return String(val) === "--" ? "0" : "1";
        }

        // ✅ Fix: use \t prefix trick instead of ="..." to preserve formatting
        // without triggering Excel's format mismatch warning
        if (
          [
            "MID",
            "TID",
            "PAN",
            "date",
            "time",
            "BatchNo",
            "BatchID",
            "RRN",
            "AuthNumber",
            "ResponseCode",
            "entryMode",
            "STAN",
          ].includes(field)
        ) {
          return `\t${String(val)}`;
        }

        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    });

    const BOM = "\uFEFF"; // ✅ UTF-8 BOM for Excel compatibility
    const csvString = BOM + csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `transactions_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDailyReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    // Format dates as YYYY-MM-DD
    const formatDate = (d) => {
      const date = new Date(d);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    const startDate = formatDate(dateRange[0]);
    const endDate = formatDate(dateRange[1]);

    setDvtLoading(true);
    try {
      const blob = await getDailyVolumeReportApi(startDate, endDate);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `daily_volume_report_${startDate}_to_${endDate}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || "Failed to generate report");
    } finally {
      setDvtLoading(false);
    }
  };
  const booleanBody = (rowData, field) => {
    const val = rowData[field];
    if (val === "--") return "--";
    return Number(val) === 1 ? "True" : "False";
  };
  const handleGetMessages = async (rowData) => {
    if (!rowData.mongoID || rowData.mongoID === "--") {
      alert("No MongoDB ID available for this transaction");
      return;
    }
    console.log("Get Messages for:", rowData);
    try {
      const res = await getTransactionMessages(rowData.mongoID);
      if (res?.isSuccess) {
      } else {
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
    }
  };
  const columns = useMemo(
    () => [
      { field: "MID", header: "MID", style: { width: "200px" } },
      { field: "TID", header: "TID", style: { width: "160px" } },
      { field: "PAN", header: "PAN", style: { width: "160px" } },
      { field: "Amount", header: "Amount (PKR)" },
      { field: "BatchNo", header: "Batch No" },
      { field: "CardScheme", header: "Card Scheme", style: { width: "140px" } },
      { field: "TransactionType", header: "Type", style: { width: "120px" } },
      { field: "date", header: "Date", style: { width: "160px" } },
      { field: "time", header: "Time", style: { width: "120px" } },
      { field: "ResponseCode", header: "Resp Code", style: { width: "120px" } },
      { field: "STAN", header: "Invoice No", style: { width: "120px" } },
      { field: "RRN", header: "RRN", style: { width: "120px" } },
      { field: "AuthNumber", header: "Auth Code", style: { width: "120px" } },
      {
        field: "SettlementStatus",
        header: "Settlement Status",
        style: { width: "120px" },
        body: (rowData) => booleanBody(rowData, "SettlementStatus"),
      },
      {
        field: "VoidStatus",
        header: "Void Status",
        style: { width: "120px" },
        body: (rowData) => booleanBody(rowData, "VoidStatus"),
      },
      {
        field: "isReversed",
        header: "Is Reversed",
        style: { width: "120px" },
        body: (rowData) => booleanBody(rowData, "isReversed"),
      },
      {
        field: "action",
        header: "Action",
        style: { width: "150px" },
        body: (rowData) => (
          <button
            className="action-btn get-messages"
            onClick={() => handleGetMessages(rowData)}
          >
            Get Messages
          </button>
        ),
      },
    ],
    [],
  );

  // if (loading) {
  //   return (
  //     <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
  //       <p className="loader">Loading reporting data...</p>
  //     </div>
  //   );
  // }
  if (loading) {
    return (
      <div className={`db-container ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="db-loader">
          <div className="loader-spinner" />
          <p>Loading reporting data…</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {/* Branch Button */}
        <button
          type="button"
          onClick={() => setModeType("DVR")}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: 10,
            border: modeType === "DVR" ? "none" : "1px solid #e0e0e0",
            background:
              modeType === "DVR"
                ? "linear-gradient(135deg, #00015e , #00015e )"
                : "#f8f9fb",
            color: modeType === "DVR" ? "#fff" : "#555",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              modeType === "DVR"
                ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                : "none",
            transition: "all 0.25s ease",
          }}
        >
          <FaChartBar style={{ width: 70 }} />
          <div className="card-content">
            <h3>Daily Volume Reports</h3>
            <p>Generate and export Volume Report</p>
          </div>
        </button>

        {/* Admin Button */}
        <button
          type="button"
          onClick={() => setModeType("Transactions")}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: 10,
            border: modeType === "Transactions" ? "none" : "1px solid #e0e0e0",
            background:
              modeType === "Transactions"
                ? "linear-gradient(135deg, #00015e , #00015e )"
                : "#f8f9fb",
            color: modeType === "Transactions" ? "#fff" : "#555",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              modeType === "Transactions"
                ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                : "none",
            transition: "all 0.25s ease",
          }}
        >
          <FaChartBar style={{ width: 70 }} />
          <div className="card-content">
            <h3>Transaction Reports</h3>
            <p>Filter and export transaction history</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setModeType("Merchants")}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: 10,
            border: modeType === "Merchants" ? "none" : "1px solid #e0e0e0",
            background:
              modeType === "Merchants"
                ? "linear-gradient(135deg, #00015e , #00015e )"
                : "#f8f9fb",
            color: modeType === "Merchants" ? "#fff" : "#555",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              modeType === "Merchants"
                ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                : "none",
            transition: "all 0.25s ease",
          }}
        >
          <FaStore style={{ width: 70 }} />
          <div className="card-content">
            <h3>Merchant Reports</h3>
            <p>View reports by merchant ID</p>
          </div>
        </button>
      </div>

      {modeType === "Transactions" && (
        <>
          <form
            onSubmit={formik.handleSubmit}
            className="reporting-form"
            noValidate
          >
            {/* MID */}
            <div className="form-group">
              <label>MID (15 digits)</label>
              <div className="input-with-icon">
                <FaStore className="input-icon" />
                <Dropdown
                  name="mid"
                  value={formik.values.mid}
                  options={merchants.map((m) => ({
                    label: m.MID,
                    value: m.MID,
                  }))}
                  onChange={(e) => {
                    formik.setFieldValue("mid", e.value);
                    formik.setFieldValue("tid", "");
                  }}
                  placeholder="Select MID"
                  className="form-input"
                  filter
                  showClear
                  filterBy="label"
                />
              </div>
              {formik.touched.mid && formik.errors.mid && (
                <p className="error-message">{formik.errors.mid}</p>
              )}
            </div>

            {/* TID */}
            <div className="form-group">
              <label>TID (8 digits)</label>
              <div className="input-with-icon">
                <FaCreditCard className="input-icon" />
                <Dropdown
                  name="tid"
                  value={formik.values.tid}
                  options={tids}
                  onChange={(e) => formik.setFieldValue("tid", e.value)}
                  placeholder="Select TID"
                  className="form-input"
                  filter
                  showClear
                  disabled={!formik.values.mid}
                  filterBy="label"
                />
              </div>
              {formik.touched.tid && formik.errors.tid && (
                <p className="error-message">{formik.errors.tid}</p>
              )}
            </div>

            <div className="form-group">
              <label>Transaction Type *</label>
              <Dropdown
                name="transactionType"
                value={formik.values.transactionType}
                options={transactionOptions}
                onChange={(e) =>
                  formik.setFieldValue("transactionType", e.value)
                }
                onBlur={() => formik.setFieldTouched("transactionType", true)}
                placeholder="-- Select Transaction --"
                className="form-input"
                showClear
              />
              {formik.touched.transactionType &&
                formik.errors.transactionType && (
                  <p className="error-message">
                    {formik.errors.transactionType}
                  </p>
                )}
            </div>

            <div className="form-actions">
              <button type="submit" className="report-btn apply">
                Apply Filter
              </button>
            </div>

            {/* ✅ CSV Export with Date Range */}

            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <label style={{ fontWeight: 600, marginTop: "15px" }}>
                Export by Date Range:
              </label>

              {/* <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            // showIcon
            dateFormat="yy-mm-dd"
            maxDate={new Date()} // Prevents selecting future years
            placeholder="Select date range"
            panelClassName="custom-calendar-panel"
            inputClassName="custom-calendar-input"
            style={{ width: "300px" }}
          /> */}
              <Calendar
                value={dateRange}
                onChange={(e) => setDateRange(e.value)}
                selectionMode="range"
                readOnlyInput
                dateFormat="yy-mm-dd"
                maxDate={new Date()}
                yearRange={`2000:${new Date().getFullYear()}`}
                // yearNavigator
                // monthNavigator
                placeholder="Select date range"
                panelClassName="custom-calendar-panel"
                inputClassName="custom-calendar-input"
                style={{ width: "300px" }}
              />
              <div className="form-actions">
                <button
                  type="button"
                  className="csv"
                  onClick={handleExportCSV}
                  disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                >
                  Export CSV
                </button>
              </div>
            </div>
          </form>
          {/* Table */}
          <section className="reporting-table-section">
            <DataTable
              value={filteredData}
              dataKey="ID"
              className="dashboard-table"
              responsiveLayout="scroll"
              paginator
              rows={10}
              paginatorClassName="custom-paginator"
            >
              {columns.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  style={col.style}
                  bodyClassName={col.bodyClassName}
                  body={col.body}
                />
              ))}
            </DataTable>
          </section>
        </>
      )}
      {modeType === "DVR" && (
        <div className="reporting-form">
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <label style={{ fontWeight: 600, marginTop: "15px" }}>
              Export by Date Range:
            </label>
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value)}
              selectionMode="range"
              readOnlyInput
              dateFormat="yy-mm-dd"
              maxDate={new Date()}
              yearRange={`2000:${new Date().getFullYear()}`}
              placeholder="Select date range"
              panelClassName="custom-calendar-panel"
              inputClassName="custom-calendar-input"
              style={{ width: "300px" }}
            />
            <div className="form-actions">
              <button
                type="button"
                className="csv"
                onClick={handleDailyReport}
                disabled={
                  !dateRange || !dateRange[0] || !dateRange[1] || dvtLoading
                }
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {dvtLoading ? (
                  <>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        border: "2px solid #fff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 0.7s linear infinite",
                      }}
                    />
                    Generating...
                  </>
                ) : (
                  "Export Daily Volume Report"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {modeType === "Merchants" && (
        <>
          <div className="reporting-form">
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <div
                style={{ display: "flex", flexDirection: "row", gap: "10px" }}
              >
                <div className="form-group">
                  <label>MID (15 digits)</label>
                  <div className="input-with-icon">
                    <FaStore className="input-icon" />
                    <Dropdown
                      name="mid"
                      value={formik.values.mid}
                      options={merchants.map((m) => ({
                        label: m.MID,
                        value: m.MID,
                      }))}
                      onChange={(e) => {
                        formik.setFieldValue("mid", e.value);
                        formik.setFieldValue("tid", "");
                        setMerchantReport(null);
                      }}
                      placeholder="Select MID"
                      className="form-input"
                      filter
                      showClear
                      filterBy="label"
                    />
                  </div>
                  {formik.touched.mid && formik.errors.mid && (
                    <p className="error-message">{formik.errors.mid}</p>
                  )}
                </div>

                {/* TID */}
                <div className="form-group">
                  <label>TID (8 digits)</label>
                  <div className="input-with-icon">
                    <FaCreditCard className="input-icon" />
                    <Dropdown
                      name="tid"
                      value={formik.values.tid}
                      options={tids}
                      onChange={(e) => formik.setFieldValue("tid", e.value)}
                      placeholder="Select TID"
                      className="form-input"
                      filter
                      showClear
                      disabled={!formik.values.mid}
                      filterBy="label"
                    />
                  </div>
                  {formik.touched.tid && formik.errors.tid && (
                    <p className="error-message">{formik.errors.tid}</p>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="report-btn apply"
                  disabled={
                    !formik.values.mid ||
                    !formik.values.tid ||
                    merchantReportLoading
                  }
                  onClick={handleFetchMerchantReport}
                >
                  {merchantReportLoading ? "Loading..." : "Load Report"}
                </button>
              </div>
            </div>
          </div>

          {merchantReport?.summary ? (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  margin: "20px 0",
                  backgroundClip: "red",
                }}
              >
                {[
                  {
                    label: "Total Batches",
                    value: merchantReport.summary.totalBatches,
                  },
                  {
                    label: "Open Batches",
                    value: merchantReport.summary.openBatches,
                  },
                  {
                    label: "Closed Batches",
                    value: merchantReport.summary.closedBatches,
                  },
                  {
                    label: "Force Settled",
                    value: merchantReport.summary.forceSettledBatches,
                  },
                  {
                    label: "Normal Settled",
                    value: merchantReport.summary.normalSettledBatches,
                  },
                  {
                    label: "Total Transactions",
                    value: merchantReport.summary.totalTransactions,
                  },
                  {
                    label: "Total Volume (PKR)",
                    value: merchantReport.summary.totalVolume?.toLocaleString(),
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    style={{
                     
                      borderRadius: 10,
                      padding: "12px 18px",
                      minWidth: 110,
                      background: '#efeff3',

                    }}
                  >
                    <p style={{ margin: 0, fontSize: 12, color: "#888" ,textAlign:'center'}}>
                      {card.label}
                    </p>
                    <p style={{ margin: 0, fontSize: 20, fontWeight: 600,textAlign:'center' }}>
                      {card.value}
                    </p>
                  </div>
                ))}
              </div>
              <section className="reporting-table-section">
                {/* Batch Table */}
                <DataTable
                  value={merchantReport.data}
                  dataKey="BatchID"
                  className="dashboard-table"
                  responsiveLayout="scroll"
                  paginator
                  rows={10}
                  paginatorClassName="custom-paginator"
                >
                  <Column field="BatchNo" header="Batch No" />
                  <Column
                    field="batchStatus"
                    header="Status"
                    body={(row) => (
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: 20,
                          fontSize: 12,
                          background:
                            row.batchStatus === "Open" ? "#e6f4ea" : "#fce8e6",
                          color:
                            row.batchStatus === "Open" ? "#2e7d32" : "#c62828",
                        }}
                      >
                        {row.batchStatus}
                      </span>
                    )}
                  />
                  <Column
                    field="settlementType"
                    header="Settlement Type"
                    body={(row) => row.settlementType ?? "—"}
                  />
                  <Column field="transactionCount" header="Transactions" />
                  <Column
                    field="totalVolume"
                    header="Volume (PKR)"
                    body={(row) => Number(row.totalVolume).toLocaleString()}
                  />
                  <Column field="voidedCount" header="Voided" />
                  <Column
                    field="createdAt"
                    header="Created At"
                    body={(row) => new Date(row.createdAt).toLocaleDateString()}
                  />
                </DataTable>
              </section>
            </>
          ):( <p1>Nothing to show</p1>)}
        </>
      )}
    </div>
  );
};

export default Reporting;
